import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Gift } from "lucide-react";
import { format } from "date-fns";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";

/**
 * Step 5 of the Booking Wizard: order summary, voucher entry/applied state,
 * and the running total. All state is owned by the parent.
 */
export default function BookingStep5Confirm({
  selectedServices,
  selectedDate,
  selectedSlot,
  totalDuration,
  totalPrice,
  newClientDiscount,
  clientName,
  clientEmail,
  addOnSelections,
  getServiceTotal,
  voucherCode,
  setVoucherCode,
  voucherApplied,
  setVoucherApplied,
  voucherLoading,
  setVoucherLoading,
}) {
  const applyVoucher = async () => {
    if (!voucherCode.trim()) return;
    setVoucherLoading(true);
    try {
      const effectiveTotal = Math.max(0, totalPrice - newClientDiscount);
      const cartServices = selectedServices.map(s => ({
        service_id: s.id, name: s.name, price: getServiceTotal(s),
      }));
      // Try as a gift/reward voucher first, then fall back to a marketing promo code —
      // customers just enter whichever code they have into the one field.
      const res = await axios.post(`${API}/vouchers/validate`, {
        code: voucherCode, total: effectiveTotal, services: cartServices,
      });
      if (res.data.valid) {
        setVoucherApplied(res.data);
        toast.success(`Voucher applied: -£${res.data.amount_to_apply?.toFixed(2)}`);
        setVoucherLoading(false);
        return;
      }
      const promoRes = await axios.post(`${API}/promo-codes/validate`, {
        code: voucherCode, total: effectiveTotal,
      });
      if (promoRes.data.valid) {
        setVoucherApplied(promoRes.data);
        toast.success(`Promo code applied: -£${promoRes.data.amount_to_apply?.toFixed(2)}`);
      } else {
        toast.error(promoRes.data.error || res.data.error || 'Invalid code');
      }
    } catch (e) {
      toast.error('Failed to validate code');
    }
    setVoucherLoading(false);
  };

  return (
    <div className="fade-in">
      <h2 className="text-2xl font-semibold tracking-tight mb-1" style={{ fontFamily: 'Playfair Display' }}>Confirm your booking</h2>
      <p className="text-sm text-muted-foreground mb-6">Review the details below</p>

      <Card className="p-6 rounded-2xl border shadow-[0_10px_30px_rgba(43,18,27,0.06)]">
        <div className="space-y-4">
          <div>
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Services</p>
            {selectedServices.map(s => {
              const sel = addOnSelections[s.id] || {};
              const svcTotal = getServiceTotal(s);
              return (
                <div key={s.id} className="py-1">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium">{s.name}</span>
                    <span className="text-sm text-muted-foreground">£{svcTotal.toFixed(2)}</span>
                  </div>
                  {sel.length && (
                    <p className="text-[10px] text-muted-foreground ml-2">Length: {sel.length}{sel.lengthUplift > 0 ? ` (+£${sel.lengthUplift.toFixed(2)})` : ''}</p>
                  )}
                  {(sel.extras || []).map(e => (
                    <p key={e.name} className="text-[10px] text-muted-foreground ml-2">+ {e.name} (+£{e.price.toFixed(2)})</p>
                  ))}
                </div>
              );
            })}
          </div>
          <hr className="border-border" />
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-xs text-muted-foreground">Date & Time</p>
              <p className="font-medium">{selectedDate && format(selectedDate, 'EEE, d MMM yyyy')}</p>
              <p className="font-medium">{selectedSlot?.time}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Duration</p>
              <p className="font-medium">{totalDuration} minutes</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Name</p>
              <p className="font-medium">{clientName}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Email</p>
              <p className="font-medium">{clientEmail}</p>
            </div>
          </div>
          <hr className="border-border" />
          {newClientDiscount > 0 ? (
            <>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Subtotal</span>
                <span className="text-sm tabular-nums">£{totalPrice.toFixed(2)}</span>
              </div>
              <div className="flex justify-between items-center py-2 px-3 bg-green-50 border border-green-200 rounded-xl -mx-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm">🎉</span>
                  <span className="text-sm font-medium text-green-700">New client discount</span>
                </div>
                <span className="text-sm font-semibold text-green-700 tabular-nums">-£{newClientDiscount.toFixed(2)}</span>
              </div>
              <div className="flex justify-between items-center pt-1">
                <span className="font-semibold" style={{ fontFamily: 'Playfair Display' }}>Total to pay</span>
                <span className="text-xl font-semibold text-primary" style={{ fontFamily: 'Playfair Display' }}>£{Math.max(0, totalPrice - newClientDiscount).toFixed(2)}</span>
              </div>
            </>
          ) : (
            <div className="flex justify-between items-center">
              <span className="font-semibold" style={{ fontFamily: 'Playfair Display' }}>Total</span>
              <span className="text-xl font-semibold text-primary" style={{ fontFamily: 'Playfair Display' }}>£{totalPrice.toFixed(2)}</span>
            </div>
          )}
        </div>
      </Card>

      {/* Voucher / promo code card */}
      <Card className="p-4 rounded-2xl border mt-3">
        <div className="flex items-center gap-2 mb-2">
          <Gift size={16} className="text-primary" />
          <span className="text-sm font-semibold" style={{ fontFamily: 'Playfair Display' }}>Have a voucher or promo code?</span>
        </div>
        {voucherApplied ? (
          <div
            className={`flex items-center justify-between p-3 rounded-xl border ${voucherApplied.source === 'vip_reward' ? 'bg-amber-50 border-amber-300' : voucherApplied.source === 'promo_code' ? 'bg-purple-50 border-purple-200' : 'bg-green-50 border-green-200'}`}
            data-testid="voucher-applied-card"
          >
            <div>
              <p className={`text-sm font-medium ${voucherApplied.source === 'vip_reward' ? 'text-amber-800' : voucherApplied.source === 'promo_code' ? 'text-purple-700' : 'text-green-700'}`}>
                {voucherApplied.source === 'vip_reward' ? '✨ VIP reward applied' : voucherApplied.source === 'promo_code' ? '🏷️ Promo code applied' : '✅ Voucher applied'}: {voucherApplied.code}
              </p>
              <p className={`text-xs ${voucherApplied.source === 'vip_reward' ? 'text-amber-700' : voucherApplied.source === 'promo_code' ? 'text-purple-600' : 'text-green-600'}`}>
                {voucherApplied.discount_type === 'percent'
                  ? `${voucherApplied.discount_percent}% off — saving £${voucherApplied.amount_to_apply?.toFixed(2)}`
                  : voucherApplied.discount_type === 'free_service'
                    ? `Free ${voucherApplied.free_service_name || 'service'} — saving £${voucherApplied.amount_to_apply?.toFixed(2)}`
                    : `-£${voucherApplied.amount_to_apply?.toFixed(2)} off your total`}
              </p>
            </div>
            <Button
              variant="ghost" size="sm"
              onClick={() => { setVoucherApplied(null); setVoucherCode(''); }}
              className="rounded-lg text-xs text-destructive"
              data-testid="voucher-remove-btn"
            >Remove</Button>
          </div>
        ) : (
          <div className="flex gap-2">
            <Input
              value={voucherCode}
              onChange={e => setVoucherCode(e.target.value.toUpperCase())}
              placeholder="Enter voucher or promo code"
              className="rounded-xl text-sm font-mono"
              data-testid="voucher-promo-code-input"
            />
            <Button
              variant="secondary"
              onClick={applyVoucher}
              disabled={voucherLoading || !voucherCode.trim()}
              className="rounded-xl shrink-0"
              data-testid="voucher-promo-apply-btn"
            >
              {voucherLoading ? '...' : 'Apply'}
            </Button>
          </div>
        )}
      </Card>

      {/* Updated total with voucher */}
      {voucherApplied && (
        <Card className="p-4 rounded-2xl border mt-2">
          <div className="flex justify-between items-center">
            <span className="font-semibold" style={{ fontFamily: 'Playfair Display' }}>Amount due</span>
            <span className="text-xl font-semibold text-primary" style={{ fontFamily: 'Playfair Display' }}>
              £{Math.max(0, totalPrice - newClientDiscount - (voucherApplied.amount_to_apply || 0)).toFixed(2)}
            </span>
          </div>
        </Card>
      )}
    </div>
  );
}
